'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import {
	configureMetaPixel,
	getPageTypeFromPathname,
	installWorkmanAnalytics,
	trackAnalyticsEvent,
} from '@/lib/analytics/client';

export function StorefrontAnalytics({ metaPixelId }: { metaPixelId?: string | null }) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const search = searchParams.toString();
	const lastTrackedPathRef = useRef<string | null>(null);

	useEffect(() => {
		configureMetaPixel(metaPixelId);
		installWorkmanAnalytics();
	}, [metaPixelId]);

	useEffect(() => {
		const nextPath = getPageViewRouteKey(pathname, search);
		if (lastTrackedPathRef.current === nextPath) return;

		lastTrackedPathRef.current = nextPath;
		const frame = window.requestAnimationFrame(() => {
			trackAnalyticsEvent({
				name: 'page_view',
				pageType: getPageTypeFromPathname(pathname),
				source: 'route_change',
			});
		});

		return () => window.cancelAnimationFrame(frame);
	}, [pathname, search]);

	return null;
}

function getPageViewRouteKey(pathname: string, search: string) {
	if (!search) return pathname;

	const params = new URLSearchParams(search);
	if (isProductPath(pathname)) {
		params.delete('variant');
		for (const key of Array.from(params.keys())) {
			if (key.startsWith('option.')) params.delete(key);
		}
	}
	params.sort();

	const normalizedSearch = params.toString();
	return normalizedSearch ? `${pathname}?${normalizedSearch}` : pathname;
}

function isProductPath(pathname: string) {
	return /\/products\/[^/]+\/?$/.test(pathname);
}
